import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Container,
    Icon,
    MenuItem,
    Paper,
    RichTextArea,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    TextInput,
    Tooltip,
} from 'shell/components';
import { DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount, getTooltipComponent } from 'utils/helper';

const TabletForm = ({ form, errors, onChange, onClickAddCriterion, onClickDeleteCriterion }) => {
    const { t } = useTranslation('assignment');

    return (
        <CustomContainer device={'tablet'}>
            <RootStack alignItems={'flex-start'} spacing={5}>
                <Stack spacing={2}>
                    <Stack direction={'row'} spacing={4}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t('rubric.create-ai-rubric.form.title', 'Create a Rubric with AI')}
                        </Text>
                        {/* <Button startIcon={<Icon kind={'video'} />} variant={'borderless'}>
                            Best Practices for Rubric Creation
                        </Button> */}
                    </Stack>
                    <Text>
                        {t(
                            'rubric.create-ai-rubric.form.description',
                            'Our AI leverages your assignment title and description to generate a rubric. You have the option to specify criteria manually or allow the AI to suggest criteria based on this information.'
                        )}{' '}
                    </Text>
                </Stack>
                <TextInput
                    sx={{ width: '650px' }}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                    label={t('assignment-title-label', 'Assignment Title')}
                    placeholder={t(
                        'rubric.create-ai-rubric.form.assignment-title-placeholder',
                        'ex. Create a Marketing Plan'
                    )}
                    required
                    value={form.title}
                    onChange={(e) => onChange('title', e.target.value)}
                />
                <RichTextArea
                    sx={{ width: '650px' }}
                    error={Boolean(errors.description)}
                    label={t('assignment-description-label', 'Assignment Description')}
                    helperText={
                        <Stack component={'span'} direction={'row-reverse'} justifyContent={'space-between'}>
                            <Text color={'inherit'} component={'span'} variant={'p3'}>
                                {t('remaining-word-count', '{{wordLimitStatus}} words', {
                                    wordLimitStatus: `${getHTMLWordCount(form.description)}/${DESCRIPTION_WORD_COUNT_LIMIT}`,
                                })}{' '}
                            </Text>
                            <Text color={'inherit'} component={'span'} variant={'p3'}>
                                {errors.description}
                            </Text>
                        </Stack>
                    }
                    helperTextPosition={'end'}
                    placeholder={t(
                        'rubric.create-ai-rubric.form.description-placeholder',
                        'Provide an overview of the tasks, requirements, and expectations for this assignment. This will be used as context to help our AI generate an accurate rubric for this assignment.'
                    )}
                    required
                    value={form.description}
                    onChange={(html) => onChange('description', html)}
                />
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'flex-end'}
                    direction={'row'}
                    justifyContent={'space-between'}
                >
                    <Stack alignItems={'flex-end'} direction={'row'} spacing={4}>
                        <CustomSelect
                            disabled={form.useCustomCriteria}
                            label={
                                <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                                    <Text>
                                        {' '}
                                        {t(
                                            'rubric.create-ai-rubric.form.number-of-criteria-label',
                                            'How many criteria will this rubric have?'
                                        )}
                                    </Text>
                                    <CustomTooltip
                                        content={getTooltipComponent(
                                            t(
                                                'rubric.create-ai-rubric.form.number-of-criteria-tooltip-title',
                                                'Creating criteria'
                                            ),
                                            t(
                                                'rubric.create-ai-rubric.form.number-of-criteria-tooltip-description',
                                                'Please indicate the number of criteria you wish to create. If you don’t choose to specify your own criteria, they will be automatically generated based on the assignment description using AI. We recommend 5 or fewer for best results.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'num-criteria-select'}
                            required
                            value={form.useCustomCriteria ? form.criteria.length.toString() : form.numCriteria}
                            onChange={(e) => onChange('numCriteria', e.target.value)}
                        >
                            <MenuItem value={'1'}>1</MenuItem>
                            <MenuItem value={'2'}>2</MenuItem>
                            <MenuItem value={'3'}>3</MenuItem>
                            <MenuItem value={'4'}>4</MenuItem>
                            <MenuItem value={'5'}>5</MenuItem>
                            <MenuItem value={'6'}>6</MenuItem>
                            <MenuItem value={'7'}>7</MenuItem>
                            <MenuItem value={'8'}>8</MenuItem>
                            <MenuItem value={'9'}>9</MenuItem>
                            <MenuItem value={'10'}>10</MenuItem>
                        </CustomSelect>
                        <Checkbox
                            aria-label={t(
                                'rubric.create-ai-rubric.form.specify-criteria-label',
                                'Specify my own criteria'
                            )}
                            checked={form.useCustomCriteria}
                            label={t('rubric.create-ai-rubric.form.specify-criteria-label', 'Specify my own criteria')}
                            onChange={(_, checked) => onChange('useCustomCriteria', checked)}
                        />
                    </Stack>
                    {form.useCustomCriteria && (
                        <Button startIcon={<Icon kind={'add'} />} variant={'secondary'} onClick={onClickAddCriterion}>
                            {t('rubric.add-criterion', 'Add Criterion')}
                        </Button>
                    )}
                </Stack>
                {form.useCustomCriteria && (
                    <Stack sx={{ width: '100%' }} spacing={4}>
                        {form.criteria.map((criterion, index) => (
                            <CustomPaper key={criterion.id} variant={'outlined'}>
                                <Stack spacing={4}>
                                    <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                        <TextInput
                                            sx={{ width: '400px' }}
                                            error={Boolean(errors.criteria?.[index]?.title)}
                                            helperText={errors.criteria?.[index]?.title}
                                            label={t(
                                                'rubric.create-ai-rubric.form.criterion-name-label',
                                                'Criterion Name'
                                            )}
                                            placeholder={t(
                                                'rubric.create-ai-rubric.form.criterion-name-placeholder',
                                                'ex. Organization'
                                            )}
                                            required
                                            value={form.criteria.find((c) => c.id === criterion.id).title}
                                            onChange={(e) => {
                                                onChange(
                                                    'criteria',
                                                    form.criteria.map((c) =>
                                                        c.id === criterion.id ? { ...c, title: e.target.value } : c
                                                    )
                                                );
                                            }}
                                        />
                                        <Button
                                            color={'neutral'}
                                            disabled={form.criteria.length <= 2}
                                            startIcon={<Icon kind={'delete'} />}
                                            variant={'tertiary'}
                                            onClick={() => onClickDeleteCriterion(criterion)}
                                        >
                                            {t('rubric.delete-criterion', 'Delete Criterion')}
                                        </Button>
                                    </Stack>
                                    <TextArea
                                        helperText={t(
                                            'rubric.create-ai-rubric.form.criterion-description-helper-text',
                                            'If left blank, the AI will create a description for this criterion'
                                        )}
                                        label={t(
                                            'rubric.create-ai-rubric.form.criterion-description-label',
                                            'Criterion Description'
                                        )}
                                        placeholder={t(
                                            'rubric.create-ai-rubric.form.criterion-description-placeholder',
                                            'ex. Evaluates the clarity and coherence of the project structure, ensuring logical arrangement and easy comprehension.'
                                        )}
                                        rows={4}
                                        value={form.criteria.find((c) => c.id === criterion.id).description}
                                        onChange={(e) => {
                                            onChange(
                                                'criteria',
                                                form.criteria.map((c) =>
                                                    c.id === criterion.id ? { ...c, description: e.target.value } : c
                                                )
                                            );
                                        }}
                                    />
                                </Stack>
                            </CustomPaper>
                        ))}
                    </Stack>
                )}
                <CustomTextInput
                    error={Boolean(errors.maxScore)}
                    helperText={errors.maxScore}
                    label={t('rubric.create-ai-rubric.form.criterion-max-score-label', 'Max score for each criterion')}
                    placeholder={t('rubric.create-ai-rubric.form.criterion-max-score-placeholder', 'ex. 25')}
                    required
                    value={form.maxScore}
                    onChange={(e) => onChange('maxScore', e.target.value)}
                />
                <CustomSelect
                    label={
                        <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                            <Text>
                                {t(
                                    'rubric.create-ai-rubric.form.criterion-levels-label',
                                    'How many rating levels will each criterion have?'
                                )}
                            </Text>{' '}
                            <CustomTooltip
                                content={getTooltipComponent(
                                    t(
                                        'rubric.create-ai-rubric.form.criterion-levels-tooltip-title',
                                        'What are rating levels?'
                                    ),
                                    t(
                                        'rubric.create-ai-rubric.form.criterion-levels-tooltip-description',
                                        'Rating levels in a rubric indicate the varying degrees of achievement or performance associated with each criterion. We recommend 8 or fewer for best results.'
                                    )
                                )}
                                type={'info'}
                            />
                        </Stack>
                    }
                    labelId={'num-options-select'}
                    required
                    value={form.numOptions}
                    onChange={(e) => onChange('numOptions', e.target.value)}
                >
                    <MenuItem value={'2'}>2</MenuItem>
                    <MenuItem value={'3'}>3</MenuItem>
                    <MenuItem value={'4'}>4</MenuItem>
                    <MenuItem value={'5'}>5</MenuItem>
                    <MenuItem value={'6'}>6</MenuItem>
                    <MenuItem value={'7'}>7</MenuItem>
                    <MenuItem value={'8'}>8</MenuItem>
                    <MenuItem value={'9'}>9</MenuItem>
                    <MenuItem value={'10'}>10</MenuItem>
                </CustomSelect>
            </RootStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(10),
        background: theme.palette.ensightful.shades[0],
    };
});

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiFormLabel-root': {
            display: 'flex',
        },
        '& > .MuiInputBase-root': {
            width: '300px',
        },
    };
});

const CustomTooltip = styled(Tooltip)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '300px',
    };
});

export default TabletForm;
